import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { z } from "zod";

import { DashboardContentTagInput } from "@/api/contentLinks";
import { mapToApiContentTag } from "@/mappers";
import {
  ContentEntityType,
  ContentLinkRef,
  LeagueReference,
  OrganizationReference,
  TournamentPickerRef,
} from "@/types";
import { getTaggableContentEntityTypes } from "@/utils/contentLink";

/**
 * Tag targets an owner may pick, one field per entity level. The set of
 * *rendered* fields depends on the owner's own level (see
 * {@link getTaggableContentEntityTypes}) — a league owner may only tag
 * tournaments. The backend re-validates level and containment on every write.
 */
export type ContentTagsFormData = {
  organizations: OrganizationReference[];
  leagues: LeagueReference[];
  tournaments: TournamentPickerRef[];
};

/** Shared by every picker: an id is what the `PUT …/tags` body is built from. */
const referenceLogo = z.string().nullable();

const organizationReferenceSchema = z.object({
  id: z.string().min(1),
  name: z.string(),
  abbreviation: z.string().nullable(),
  logo: referenceLogo,
}) satisfies z.ZodType<OrganizationReference>;

const leagueReferenceSchema = z.object({
  id: z.string().min(1),
  title: z.string(),
  logo: referenceLogo,
}) satisfies z.ZodType<LeagueReference>;

/**
 * The tournaments picker is the shared {@link TournamentsSelector}, so its value
 * is the picker ref (nullable title, optional `categoryIds`) rather than the
 * logo-carrying reference the other two pickers store. Only the id reaches the
 * API either way.
 */
const tournamentPickerRefSchema = z.object({
  id: z.string().min(1),
  title: z.string().nullable(),
  categoryIds: z.array(z.string()).optional(),
}) satisfies z.ZodType<TournamentPickerRef>;

const contentTagsSchema = z.object({
  organizations: z.array(organizationReferenceSchema),
  leagues: z.array(leagueReferenceSchema),
  tournaments: z.array(tournamentPickerRefSchema),
});

const emptyContentTags: ContentTagsFormData = {
  organizations: [],
  leagues: [],
  tournaments: [],
};

const contentTagsFromLinks = (tags: ContentLinkRef[]): ContentTagsFormData => ({
  organizations: tags
    .filter((tag) => tag.entityType === "federation")
    .map((tag) => ({
      id: tag.entityId,
      name: tag.title ?? "",
      abbreviation: null,
      logo: tag.logo,
    })),
  leagues: tags
    .filter((tag) => tag.entityType === "league")
    .map((tag) => ({ id: tag.entityId, title: tag.title ?? "", logo: tag.logo })),
  tournaments: tags
    .filter((tag) => tag.entityType === "tournament")
    .map((tag) => ({ id: tag.entityId, title: tag.title })),
});

/** Form selection → the declarative `PUT …/tags` body. */
export const buildContentTagsInput = (
  values: ContentTagsFormData,
  taggableTypes: ContentEntityType[]
): DashboardContentTagInput[] =>
  [
    ...(taggableTypes.includes("federation")
      ? values.organizations.map((organization) => ({
          entityType: "federation" as const,
          entityId: organization.id,
        }))
      : []),
    ...(taggableTypes.includes("league")
      ? values.leagues.map((league) => ({ entityType: "league" as const, entityId: league.id }))
      : []),
    ...(taggableTypes.includes("tournament")
      ? values.tournaments.map((tournament) => ({
          entityType: "tournament" as const,
          entityId: tournament.id,
        }))
      : []),
  ].map(mapToApiContentTag);

/**
 * Owner-side tag management, shared by the news and album create / update
 * pages. `owner` is the content's single owner entity; only strictly lower
 * levels can be tagged.
 */
export const useContentTagsForm = ({
  owner,
  tags,
  onSubmit,
}: {
  owner: ContentEntityType;
  tags?: ContentLinkRef[];
  onSubmit?: (tags: DashboardContentTagInput[]) => void;
}) => {
  const taggableTypes = getTaggableContentEntityTypes(owner);
  const defaultValues = tags ? contentTagsFromLinks(tags) : emptyContentTags;

  const form = useForm<ContentTagsFormData>({
    defaultValues,
    resolver: zodResolver(contentTagsSchema) as never,
  });

  return {
    form,
    taggableTypes,
    /** True when the owner sits at the bottom of the hierarchy and can tag nothing. */
    hasNoTaggableTargets: taggableTypes.length === 0,
    getSubmittedValues: () => buildContentTagsInput(form.getValues(), taggableTypes),
    submit: form.handleSubmit((values) => onSubmit?.(buildContentTagsInput(values, taggableTypes))),
    isDirty: form.formState.isDirty,
    resetToTags: (next: ContentLinkRef[]) => form.reset(contentTagsFromLinks(next)),
  };
};

export type ContentTagsFormControl = ReturnType<typeof useContentTagsForm>;
